import { isUndefined } from 'lodash';
import { BufferGeometry, Line, Vector3 } from 'three';
import { assertDefined } from '../../../../cross-team-shared/misc-utils/strict-record';
import { H3BasicMaterial } from '../../../materials/basic/h3-basic-material';
import { CuboidLocationState3D } from '../../../state/cuboid-location-state-3d';
import {
  getFocusedCuboidLocation,
  getFocusedShapeIndex,
} from '../../../state/focused-shape-utils';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { MaterialService } from '../../material.service';
import { RendererLayer, setRendererLayers } from '../../render-utils';
import {
  AbstractMeshHelper,
  ConfigureMeshesConfiguration,
} from '../abstract-mesh-helper';

export class CuboidAxesMeshHelper extends AbstractMeshHelper {
  private xAxis: Line<BufferGeometry>;
  private yAxis: Line<BufferGeometry>;
  private zAxis: Line<BufferGeometry>;
  private reUsablePoints = [new Vector3(), new Vector3()];

  constructor(private shapeIndex: number) {
    super();
    this.xAxis = new Line(new BufferGeometry());
    this.yAxis = new Line(new BufferGeometry());
    this.zAxis = new Line(new BufferGeometry());

    this.xAxis.frustumCulled = false;
    this.yAxis.frustumCulled = false;
    this.zAxis.frustumCulled = false;

    this.xAxis.name = 'x';
    this.yAxis.name = 'y';
    this.zAxis.name = 'z';

    const userData = { meshType: 'cuboid-axis' };
    this.xAxis.userData = userData;
    this.yAxis.userData = userData;
    this.zAxis.userData = userData;
  }

  override getMeshes() {
    const meshesByAxisOrder = [this.xAxis, this.yAxis, this.zAxis];
    return meshesByAxisOrder;
  }

  override dispose() {
    this.getMeshes().forEach((mesh) => mesh.geometry.dispose());
  }

  protected override updateRendererLayers(state: PointCloudContentState) {
    setRendererLayers([], this.getMeshes());

    const { highlightedTranslationAxis } = state.editPanel;
    const activePanel = state.render.currentEditSource?.index;
    const isFocused = getFocusedShapeIndex(state) === this.shapeIndex;
    if (!isFocused) {
      return;
    } else if (isUndefined(activePanel)) {
      return;
    } else if (isUndefined(highlightedTranslationAxis)) {
      return;
    }

    const panelLayerByAxis = {
      0: RendererLayer.SideViewBottom,
      1: RendererLayer.SideViewMiddle,
      2: RendererLayer.SideViewTop,
    };
    const highlightedAxisMesh = this.getMeshes()[highlightedTranslationAxis];
    setRendererLayers([panelLayerByAxis[activePanel]], highlightedAxisMesh);
  }

  protected override configureMeshes({
    materialService,
    state,
  }: ConfigureMeshesConfiguration) {
    const location = getFocusedCuboidLocation(state);
    assertDefined(location);

    this.positionLines(location);
    this.styleLines(materialService);
  }

  private positionLines({ position, rotation }: CuboidLocationState3D) {
    this.getMeshes().forEach((mesh, axis) => {
      this.reUsablePoints.forEach((vector) => vector.set(0, 0, 0));

      const arbitraryLargeNumber = 10_000;
      this.reUsablePoints[0].setComponent(axis, arbitraryLargeNumber);
      this.reUsablePoints[1].setComponent(axis, -arbitraryLargeNumber);

      mesh.geometry.setFromPoints(this.reUsablePoints);
      mesh.position.fromArray(position);
      mesh.rotation.fromArray(rotation);

      mesh.computeLineDistances();
    });
  }

  private styleLines(materialService: MaterialService) {
    this.xAxis.material = materialService.get(H3BasicMaterial, {
      color: '#FF0000',
      name: 'x-axis',
      depthTest: false,
    });
    this.yAxis.material = materialService.get(H3BasicMaterial, {
      color: '#00FF00',
      name: 'y-axis',
      depthTest: false,
    });
    this.zAxis.material = materialService.get(H3BasicMaterial, {
      color: '#0000FF',
      name: 'z-axis',
      depthTest: false,
    });
  }
}
